import { Controller } from "stimulus";
import t from "../i18n";

export default class extends Controller {
  static targets = [
    "aiButton",
    "bodyEditor",
    "helperText",
    "leadId",
    "leadLink",
    "leadResult",
    "progressBar",
    "progressDetail",
    "progressPanel",
    "progressPercent",
    "recipient",
    "selectedAccount",
    "selectedLeadName",
    "selectedProject",
    "sender",
    "statusDetail",
    "statusLabel",
    "subject",
    "submitButton"
  ];

  connect() {
    this.busy = false;
    this.updateControls();
  }

  disconnect() {
    window.clearInterval(this.progressTimer);
  }

  selectLead(event) {
    const item = event.currentTarget;
    if (!item.dataset.leadId) return;

    event.preventDefault();
    this.leadResultTargets.forEach((target) => target.classList.remove("is-active"));
    item.classList.add("is-active");

    this.leadIdTarget.value = item.dataset.leadId;
    this.recipientTarget.value = item.dataset.leadEmail || "";
    this.senderTarget.value = item.dataset.leadAccount || "";
    this.selectedLeadNameTarget.textContent = item.dataset.leadName || "Lead";
    this.selectedProjectTarget.textContent = item.dataset.leadProject || t("mail_composer.project_fallback", {}, "Projekt");
    this.selectedAccountTarget.textContent = item.dataset.leadAccount || t("mail_composer.account_missing", {}, "Nicht verbunden");

    if (this.hasLeadLinkTarget && item.dataset.leadPath) {
      this.leadLinkTarget.href = item.dataset.leadPath;
      this.leadLinkTarget.classList.remove("is-disabled");
    }

    this.updateLeadVariableContext(item);

    if (item.dataset.leadCanSend === "true") {
      this.setStatus(t("mail_composer.status.lead_selected", {}, "Lead ausgewählt"), t("mail_composer.detail.lead_selected", {}, "Empfänger und Versandkonto wurden aus dem Projektkontext gesetzt."));
      this.helperTextTarget.textContent = t("mail_composer.helper.account_used", {}, "Dieses Konto wird für das Projekt des Leads verwendet.");
    } else {
      this.setStatus(t("mail_composer.status.account_missing", {}, "Konto fehlt"), t("mail_composer.detail.account_missing", {}, "Der Lead ist ausgewählt, aber für dieses Projekt ist kein Versandkonto verbunden."));
      this.helperTextTarget.textContent = t("mail_composer.helper.connect_account", {}, "Verbinde zuerst SMTP, Microsoft oder Google Mail in den Projekteinstellungen.");
    }

    this.updateControls();
  }

  insertPrompt(event) {
    const prompt = this.resolveVariables(event.currentTarget.dataset.prompt || "");
    if (!prompt || !this.hasBodyEditorTarget) return;

    this.focusEditor();
    const prefix = this.editorText().trim().length > 0 ? "\n\n" : "";
    this.bodyEditorTarget.editor.insertString(`${prefix}${prompt}`);
    this.setStatus(t("contact_composer.status.prompt_inserted", {}, "Prompt ergänzt"), t("mail_composer.detail.prompt_inserted", {}, "Starte die KI oder schreibe direkt weiter."));
  }

  insertTemplate(event) {
    const template = this.resolveVariables(event.currentTarget.dataset.template || "", { html: true });
    if (!template || !this.hasBodyEditorTarget) return;

    this.focusEditor();
    this.bodyEditorTarget.editor.insertHTML(template);
    this.setStatus(t("contact_composer.status.template_inserted", {}, "Textbaustein eingefügt"), t("mail_composer.detail.template_inserted", {}, "Variablen wurden mit dem ausgewählten Lead befüllt."));
  }

  sync() {
    if (this.editorText().trim().length > 0) {
      this.setStatus(t("contact_composer.status.draft_active", {}, "Entwurf aktiv"), t("mail_composer.detail.draft_active", {}, "Die Nachricht ist bereit zum Prüfen und Senden."));
    }
    this.updateControls();
  }

  async generateMessage(event) {
    event.preventDefault();
    const leadId = this.leadIdTarget.value;
    if (!leadId || this.busy || this.aiButtonTarget.disabled) return;

    const currentText = this.editorText();
    const currentHtml = this.editorHTML();
    this.setBusy(true);
    this.startProgress();
    this.setStatus(t("composer.status.ai_working", {}, "KI arbeitet"), t("mail_composer.detail.ai_working", {}, "Lead, Verlauf und Projektkontext werden analysiert."));

    try {
      const response = await fetch(`/leads/${leadId}/propose_next_message.json`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ current_text: currentText, current_html: currentHtml })
      });

      const data = await this.readJsonResponse(response);

      if (!response.ok) {
        throw new Error(data.error || t("composer.error.ai_response_load_failed", {}, "Die KI-Antwort konnte nicht geladen werden."));
      }
      if (data.error) {
        throw new Error(data.error);
      }

      const messageHTML = data.elements && data.elements[0] ? data.elements[0][1] : "";
      if (!messageHTML) {
        throw new Error(t("composer.error.ai_response_load_failed", {}, "Die KI-Antwort konnte nicht geladen werden."));
      }

      if (data.subject && this.hasSubjectTarget) {
        this.subjectTarget.value = data.subject;
      }
      if (messageHTML && this.hasBodyEditorTarget) {
        this.setProgress(82, t("composer.progress.ai_writing", {}, "KI schreibt den Vorschlag live in den Editor."));
        await this.typeMessage(messageHTML);
      }

      this.setProgress(100, t("composer.progress.suggestion_ready", {}, "Vorschlag ist bereit."));
      this.setStatus(t("mail_composer.status.ai_draft_ready", {}, "KI-Entwurf fertig"), t("mail_composer.detail.ai_draft_ready", {}, "Prüfe Betreff und Nachricht vor dem Senden."));
    } catch (error) {
      this.setStatus(t("composer.status.ai_failed", {}, "KI konnte nicht schreiben"), error.message || t("common.try_again", {}, "Bitte versuche es erneut."));
      this.setProgress(0, t("composer.progress.ai_error", {}, "Fehler bei der KI-Verarbeitung."));
    } finally {
      window.clearInterval(this.progressTimer);
      window.setTimeout(() => {
        this.setBusy(false);
        if (this.hasProgressPanelTarget) this.progressPanelTarget.classList.add("d-none");
      }, 450);
    }
  }

  updateControls() {
    const hasLead = this.hasLeadIdTarget && this.leadIdTarget.value.length > 0;
    const activeLead = this.leadResultTargets.find((target) => target.classList.contains("is-active"));
    const canSend = hasLead && activeLead && activeLead.dataset.leadCanSend === "true";

    if (this.hasAiButtonTarget) {
      this.aiButtonTarget.disabled = !hasLead || this.busy;
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !canSend || this.busy;
    }
  }

  updateLeadVariableContext(item) {
    const fields = [
      "leadSalutation",
      "leadFirstName",
      "leadLastName",
      "leadFullName",
      "leadTitle",
      "leadCompany",
      "leadPosition",
      "leadEmail",
      "leadPhone",
      "leadMobile",
      "leadCity",
      "leadZip",
      "leadStreet",
      "leadProject"
    ];

    fields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(item.dataset, field)) {
        this.element.dataset[field] = item.dataset[field] || "";
      }
    });
  }

  resolveVariables(content, options = {}) {
    const values = this.variableValues();
    return (content || "").replace(/\{\{\s*([A-Za-z0-9_-]+)\s*\}\}/g, (match, token) => {
      const value = values[token.toLowerCase().replace(/-/g, "_")];
      if (value === undefined || value === null || value === "") return match;
      return options.html ? this.escapeHtml(value) : value;
    });
  }

  variableValues() {
    const dataset = this.element.dataset;
    const fullName = dataset.leadFullName || [dataset.leadFirstName, dataset.leadLastName].filter(Boolean).join(" ");
    const senderName = [dataset.senderFirstName, dataset.senderLastName].filter(Boolean).join(" ");

    return {
      anrede_formell: this.formalSalutation(),
      formelle_anrede: this.formalSalutation(),
      anrede: dataset.leadSalutation,
      vorname: dataset.leadFirstName,
      first_name: dataset.leadFirstName,
      nachname: dataset.leadLastName,
      last_name: dataset.leadLastName,
      voller_name: fullName,
      full_name: fullName,
      titel: dataset.leadTitle,
      firma: dataset.leadCompany,
      unternehmen: dataset.leadCompany,
      organisation: dataset.leadCompany,
      position: dataset.leadPosition,
      email: dataset.leadEmail,
      e_mail: dataset.leadEmail,
      telefon: dataset.leadPhone,
      tel: dataset.leadPhone,
      mobil: dataset.leadMobile,
      mobile: dataset.leadMobile,
      stadt: dataset.leadCity,
      city: dataset.leadCity,
      plz: dataset.leadZip,
      zip: dataset.leadZip,
      strasse: dataset.leadStreet,
      street: dataset.leadStreet,
      projekt: dataset.leadProject,
      project: dataset.leadProject,
      absender_vorname: dataset.senderFirstName,
      absender_nachname: dataset.senderLastName,
      absender_name: senderName,
      absender_email: dataset.senderEmail
    };
  }

  formalSalutation() {
    const dataset = this.element.dataset;
    const title = dataset.leadTitle || "";
    const lastName = dataset.leadLastName || "";
    const nameParts = [title, lastName].filter(Boolean).join(" ");

    if (dataset.leadSalutation === "Frau") {
      return nameParts ? t("mail_composer.salutation.ms", { name: nameParts }, `Sehr geehrte Frau ${nameParts},`) : t("mail_composer.salutation.generic", {}, "Sehr geehrte Damen und Herren,");
    }
    if (dataset.leadSalutation === "Herr") {
      return nameParts ? t("mail_composer.salutation.mr", { name: nameParts }, `Sehr geehrter Herr ${nameParts},`) : t("mail_composer.salutation.generic", {}, "Sehr geehrte Damen und Herren,");
    }
    return lastName ? t("mail_composer.salutation.day_name", { name: lastName }, `Guten Tag ${lastName},`) : t("mail_composer.salutation.day", {}, "Guten Tag,");
  }

  setStatus(label, detail) {
    if (this.hasStatusLabelTarget) this.statusLabelTarget.textContent = label;
    if (this.hasStatusDetailTarget) this.statusDetailTarget.textContent = detail;
  }

  editorText() {
    if (!this.hasBodyEditorTarget || !this.bodyEditorTarget.editor) return "";

    return this.bodyEditorTarget.editor.getDocument().toString();
  }

  editorHTML() {
    if (!this.hasBodyEditorTarget) return "";

    return this.bodyEditorTarget.innerHTML || "";
  }

  async typeMessage(html) {
    const editor = this.bodyEditorTarget.editor;
    const text = this.plainTextFromHtml(html);
    const chunks = text.match(/.{1,10}(\s|$)|\S+/g) || [text];
    let visibleText = "";

    editor.loadHTML("");
    for (const chunk of chunks) {
      visibleText += chunk;
      editor.loadHTML(this.previewHtmlFromText(visibleText));
      await this.sleep(14);
    }

    if (html) {
      editor.loadHTML(this.normalizeEmailHtml(html));
    }
  }

  plainTextFromHtml(html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html || "";
    return wrapper.innerText.trim();
  }

  previewHtmlFromText(text) {
    const escaped = this.escapeHtml(text || "");
    const paragraphs = escaped
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.replace(/\n/g, "<br>"))
      .filter((paragraph) => paragraph.trim().length > 0);

    return paragraphs.length ? paragraphs.map((paragraph) => `<p>${paragraph}</p>`).join("") : "";
  }

  normalizeEmailHtml(html) {
    const wrapper = document.createElement("div");
    wrapper.innerHTML = html || "";

    if (!wrapper.querySelector("p, ul, ol, blockquote, h1, h2, h3, h4, div, br")) {
      return this.previewHtmlFromText(wrapper.textContent || "");
    }

    return wrapper.innerHTML;
  }

  escapeHtml(value) {
    const wrapper = document.createElement("div");
    wrapper.textContent = value;
    return wrapper.innerHTML;
  }

  startProgress() {
    if (!this.hasProgressPanelTarget) return;

    window.clearInterval(this.progressTimer);
    this.progressPanelTarget.classList.remove("d-none");
    this.setProgress(8, t("composer.progress.reading_context", {}, "Lead-Kontext wird gelesen."));

    const steps = [
      [28, t("composer.progress.summarizing_contacts", {}, "Letzte Kontaktpunkte werden verdichtet.")],
      [46, t("composer.progress.prioritizing_value", {}, "Nutzenargumente werden priorisiert.")],
      [63, t("composer.progress.tuning_subject_tone", {}, "Betreff und Tonalität werden abgestimmt.")],
      [74, t("composer.progress.preparing_text", {}, "Der Text wird vorbereitet.")]
    ];
    let index = 0;

    this.progressTimer = window.setInterval(() => {
      if (index >= steps.length) {
        window.clearInterval(this.progressTimer);
        return;
      }
      this.setProgress(steps[index][0], steps[index][1]);
      index += 1;
    }, 650);
  }

  setProgress(percent, detail) {
    if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${percent}%`;
    if (this.hasProgressDetailTarget) this.progressDetailTarget.textContent = detail;
    if (this.hasProgressPercentTarget) this.progressPercentTarget.textContent = `${percent}%`;
    if (this.hasStatusDetailTarget) this.statusDetailTarget.textContent = detail;
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.element.classList.toggle("is-busy", isBusy);
    this.updateControls();
  }

  focusEditor() {
    if (this.hasBodyEditorTarget && typeof this.bodyEditorTarget.focus === "function") {
      this.bodyEditorTarget.focus();
    }
  }

  csrfToken() {
    const token = document.querySelector("meta[name='csrf-token']");
    return token ? token.content : "";
  }

  async readJsonResponse(response) {
    try {
      return await response.json();
    } catch (error) {
      return {};
    }
  }

  sleep(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }
}
